/**
 * Formula evaluation engine for calculated columns
 * Supports algebraic expressions with column references like [COLUMN_NAME]
 */

import { getColumnKey } from "@/components/tableau/utils/formatters";

/**
 * Parse a numeric value from various formats
 */
const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  
  // Remove currency symbols, spaces, and convert comma to dot
  const cleaned = String(value)
    .replace(/[€$£¥]/g, '')
    .replace(/\s/g, '')
    .replace(',', '.');
  
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

/**
 * Evaluate a formula with column references
 * @param {string} formula - Formula like "[REVENUE] - [COST]"
 * @param {object} rowData - The extracted_data object for the row
 * @param {array} allColumns - All column definitions (for resolving calculated columns)
 * @param {number} depth - Recursion depth (to prevent circular references)
 * @returns {number|null} - The calculated result or null if invalid
 */
export const evaluateFormula = (formula, rowData, allColumns = [], depth = 0) => {
  if (!formula || !rowData) return null;
  if (depth > 10) return null; // Prevent infinite recursion
  
  // Normalize a string for comparison
  const normalize = (str) => str.toLowerCase().replace(/\s+/g, ' ').replace(/\.\s*/g, '.').trim();
  
  try {
    // Replace column references with values
    let expression = formula;
    const columnRefRegex = /\[([^\]]+)\]/g;
    let match;
    
    while ((match = columnRefRegex.exec(formula)) !== null) {
      const columnName = match[1];
      const normalizedName = normalize(columnName);
      
      // Flexible key resolution (same logic as getCellValue)
      const key = getColumnKey(columnName);
      let value = rowData[key];
      if (value === undefined || value === null || value === '') value = rowData[columnName];
      if (value === undefined || value === null || value === '') value = rowData[columnName.toUpperCase()];
      if (value === undefined || value === null || value === '') value = rowData[columnName.toLowerCase()];
      if (value === undefined || value === null || value === '') value = rowData[columnName.toUpperCase().replace(/\s+/g, '_')];
      if (value === undefined || value === null || value === '') value = rowData[columnName.toLowerCase().replace(/\s+/g, '.')];
      
      // Check if this column is itself a calculated column
      const columnDef = allColumns.find(c => c.name === columnName || normalize(c.name) === normalizedName);
      
      let foundOverride = false;
      
      // First, check if this column has a manual override
      const possibleOverrideKeys = [
        `_override_${columnName}`,
        columnDef ? `_override_${columnDef.name}` : null
      ].filter(Boolean);
      
      // Check direct matches first
      for (const overrideKey of possibleOverrideKeys) {
        if (rowData[overrideKey] !== undefined && rowData[overrideKey] !== null && rowData[overrideKey] !== '') {
          value = rowData[overrideKey];
          foundOverride = true;
          break;
        }
      }
      
      // If not found, search through all _override_ keys with normalized matching
      if (!foundOverride) {
        for (const key of Object.keys(rowData)) {
          if (key.startsWith('_override_')) {
            const overrideColName = key.substring('_override_'.length);
            if (normalize(overrideColName) === normalizedName) {
              value = rowData[key];
              foundOverride = true;
              break;
            }
          }
        }
      }
      
      // If no override found and it's a calculated column, recursively evaluate
      if (!foundOverride && columnDef?.type === 'calcul' && columnDef.formula) {
        value = evaluateFormula(columnDef.formula, rowData, allColumns, depth + 1);
      }
      
      const numValue = parseNumericValue(value);
      
      if (numValue === null) {
        // If any value is null, the whole formula fails
        return null;
      }
      
      // Escape special regex characters in column name for replacement
      const escapedName = columnName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expression = expression.replace(new RegExp(`\\[${escapedName}\\]`, 'g'), numValue.toString());
    }
    
    // Safety check: only allow numbers, operators, parentheses, and spaces
    if (!/^[\d\s+\-*/().]+$/.test(expression)) {
      console.warn('Formula contains invalid characters:', expression);
      return null;
    }
    
    // Evaluate the expression
    // Using Function constructor for safe evaluation (no access to global scope)
    const result = new Function(`return (${expression})`)();
    
    if (typeof result !== 'number' || !isFinite(result)) {
      return null;
    }
    
    return result;
  } catch (error) {
    console.warn('Formula evaluation error:', error);
    return null;
  }
};

/**
 * Format a calculated result based on format type
 */
export const formatCalculatedValue = (value, format = 'currency', decimalPlaces = 2) => {
  if (value === null || value === undefined) return null;
  
  if (format === 'percentage') {
    return `${value.toLocaleString('fr-FR', { 
      minimumFractionDigits: decimalPlaces, 
      maximumFractionDigits: decimalPlaces 
    })} %`;
  }
  
  // Default: currency
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(value);
};

/**
 * Extract column names from a formula
 */
export const extractColumnReferences = (formula) => {
  if (!formula) return [];
  
  const matches = formula.match(/\[([^\]]+)\]/g);
  if (!matches) return [];
  
  return matches.map(m => m.slice(1, -1));
};

/**
 * Validate a formula syntax
 */
export const validateFormula = (formula) => {
  if (!formula) return { valid: false, error: 'Formule vide' };
  
  // Check for balanced brackets
  const openBrackets = (formula.match(/\[/g) || []).length;
  const closeBrackets = (formula.match(/\]/g) || []).length;
  
  if (openBrackets !== closeBrackets) {
    return { valid: false, error: 'Crochets non équilibrés' };
  }
  
  // Check for balanced parentheses
  const openParens = (formula.match(/\(/g) || []).length;
  const closeParens = (formula.match(/\)/g) || []).length;
  
  if (openParens !== closeParens) {
    return { valid: false, error: 'Parenthèses non équilibrées' };
  }
  
  // Check for at least one column reference
  if (!formula.match(/\[[^\]]+\]/)) {
    return { valid: false, error: 'La formule doit contenir au moins une référence de colonne [NOM_COLONNE]' };
  }
  
  return { valid: true };
};
